import { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";

import { fetchAvailableTags } from "./redux/slices/availableTagsSlice";
import { fetchPermissions } from "./redux/slices/permissionsSlice";
import PrivateRoute from "./components/PrivateRoute";
import "./styles/table/global.scss";
import AppLayout from "./layout/AppLayout";
import { AuthProvider } from "./AuthContext";
import { redirectToSSOPortal } from "./utils/sso";

const DebuggerDock =
  process.env.REACT_APP_DEBUGGER === "true"
    ? require("./components/DebuggerDock/DebuggerDock").default
    : null;

// Lazy-loaded components
const TestDesignStudio = lazy(() => import("./pages/TestDesignStudio"));
const ProjectList = lazy(() => import("./pages/ProjectList"));
const TestSuiteTreeV1 = lazy(() =>
  import("./components/TestSuiteTree/TestSuiteTreeV1")
);
const ExecuteTests = lazy(() =>
  import("./components/TestSuiteTree/ExecuteTests")
);
const JobTemplates = lazy(() => import("./components/JobTemplates"));
const SuiteBuildV1 = lazy(() =>
  import("./components/SuiteBuild/SuiteBuild-v1")
);
const SuiteBuild = lazy(() => import("./components/SuiteBuild/SuiteBuild"));
const EditMultipleFiles = lazy(() =>
  import("./components/EditFiles/EditMultipleFiles")
);
const ChangeRequests = lazy(() => import("./components/ChangeRequest"));
const Project = lazy(() => import("./components/Project"));
const SessionSummary = lazy(() => import("./components/SessionSummary"));
const ExecuteTestCases = lazy(() =>
  import("./components/ExecuteTestCases/ExecuteTestCasesComponent")
);
const JobSummary = lazy(() => import("./components/JobSummary"));
const JobSummaryExport = lazy(() =>
  import("./components/Report/JobSummaryExport")
);
const SessionSummaryExport = lazy(() =>
  import("./components/Report/SessionSummaryExport")
);
const CompareReports = lazy(() =>
  import("./components/Report/CompareReports")
);
const ElementExtractor = lazy(() =>
  import("./components/ElementExtraction/ElementExtractor")
);
const TestStudio = lazy(() => import("./components/TestStudio/TestStudio"));

const App = () => {
  const dispatch = useDispatch();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramToken = params.get("token");
    const paramOrganization = params.get("organization");

    if (paramToken && paramOrganization) {
      localStorage.setItem("sso_token", paramToken);
      localStorage.setItem("organization", paramOrganization);

      // Clean up the URL so token isn't visible
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      url.searchParams.delete("organization");
      window.history.replaceState({}, document.title, url.toString());

      dispatch(fetchPermissions());
      dispatch(fetchAvailableTags());
      setInitialized(true);
    } else {
      const ssoToken = localStorage.getItem("sso_token");
      if (!ssoToken) {
        // No token anywhere, redirect go to SSO
        redirectToSSOPortal();
      } else {
        dispatch(fetchPermissions());
        dispatch(fetchAvailableTags());
        setInitialized(true);
      }
    }
  }, [dispatch]);

  if (!initialized) return <div>Loading...</div>;

  return (
    <>
      {/* DEBUGGER — global, bottom-docked */}
      {DebuggerDock && <DebuggerDock />}

      <Router>
        <AuthProvider>
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route element={<PrivateRoute />}>
                <Route element={<AppLayout />}>
                  <Route path="/" index element={<ProjectList />} />
                  <Route path="/projects" index element={<ProjectList />} />
                  <Route path="/:projectId" element={<Project />} />
                  <Route
                    path="/:projectId/suite-tree"
                  element={<ExecuteTests />}
                  />
                  <Route
                    path="/:projectId/suite-tree/v1"
                    element={<TestSuiteTreeV1 />}
                  />
                <Route
                  path="/:projectId/job-templates"
                  element={<JobTemplates />}
                />
                  <Route
                    path="/:projectId/modules"
                    element={<TestDesignStudio feature="module-studio" />}
                  />
                  <Route
                    path="/:projectId/test-design"
                    element={<TestDesignStudio feature="test-case-studio" />}
                  />
                  <Route
                    path="/:projectId/test-data-design"
                    element={<TestDesignStudio feature="test-data-studio" />}
                  />
                  <Route
                    path="/:projectId/suite-build"
                    element={<SuiteBuild />}
                  />
                  <Route
                    path="/:projectId/suite-build/v1"
                    element={<SuiteBuildV1 />}
                  />
                  <Route
                    path="/:projectId/edit-files"
                    element={<EditMultipleFiles />}
                  />
                  <Route
                    path="/:projectId/change-requests"
                    element={<ChangeRequests />}
                  />
                  <Route
                    path="/:projectId/session-summary/:sessionId"
                    element={<SessionSummary />}
                  />
                  <Route
                    path="/:projectId/test-cases/execute"
                    element={<ExecuteTestCases />}
                  />
                  <Route
                    path="/:projectId/job-summary/:jobId"
                    element={<JobSummary />}
                  />
                  <Route
                    path="/:projectId/export-job-execution/:jobId"
                    element={<JobSummaryExport />}
                  />
                  <Route
                    path="/:projectId/export-session-summary/:testId"
                    element={<SessionSummaryExport />}
                  />
                  <Route
                    path="/:projectId/job-execute/:jobId"
                    element={<ExecuteTestCases />}
                  />
                  <Route
                    path="/compare/:jobId1/:jobId2"
                    element={<CompareReports />}
                  />
                  <Route
                    path="/:projectId/element-extractor"
                    element={<ElementExtractor />}
                  />
                  <Route
                    path="/:projectId/test-case-manager"
                    element={<TestStudio />}
                  />
                </Route>
              </Route>

              {/* Redirect all other paths to root */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </>
  );
};

export default App;
